import React from 'react';
import { View, ViewStyle, StyleProp } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BORDES SEGUROS DE VERDAD (el `SafeAreaView` de react-native NO sirve aquí)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * EL FALLO QUE ESTO CIERRA — ES EL MOTIVO DEL RECHAZO vc13
 *
 * `android/gradle.properties` tiene `edgeToEdgeEnabled=true`: la app dibuja
 * POR DEBAJO de la barra de estado y de la barra de navegación. Para que eso
 * no tape nada hay que aplicar los insets a mano.
 *
 * Las 39 pantallas de la app usaban `SafeAreaView` importado de
 * `'react-native'`. Ese componente, en RN 0.85, es literalmente:
 *
 *     Platform.select({ ios: <componente nativo>, default: View })   // @deprecated
 *
 * O sea: en **Android es un `<View>` vacío**. No protege absolutamente nada.
 * La librería que sí funciona (`react-native-safe-area-context`) estaba
 * instalada desde siempre y NO se importaba en ningún archivo del proyecto.
 *
 * Consecuencia real, vista en el teléfono del fundador: la barra de escribir
 * del chat queda enterrada bajo los tres botones de Android. El campo existe,
 * se dibuja, pero el toque se lo lleva el sistema. Google lo describió como
 * "Unresponsive UI elements, such as buttons or icons".
 *
 * ── CÓMO USARLO ────────────────────────────────────────────────────────────
 *
 * Para una pantalla completa, en lugar de `<SafeAreaView>`:
 *
 *     <PantallaSegura style={styles.safeArea}>…</PantallaSegura>
 *
 * Para un elemento anclado abajo (barra de escribir, botonera), cuando no
 * quieras envolver la pantalla entera:
 *
 *     const { abajo } = useMargenesSeguros();
 *     <View style={[styles.barra, { paddingBottom: abajo + 10 }]}>
 *
 * NOTA: no hace falta montar `<SafeAreaProvider>`: expo-router ya monta
 * `SafeAreaProviderCompat` dentro de su Stack y de sus Tabs, así que
 * `useSafeAreaInsets()` devuelve valores reales tal cual.
 */

export function useMargenesSeguros() {
  const insets = useSafeAreaInsets();
  return {
    arriba: insets.top,
    abajo: insets.bottom,
    izquierda: insets.left,
    derecha: insets.right,
  };
}

type Props = {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  /** Bordes a proteger. Por defecto arriba y abajo, que es lo habitual. */
  bordes?: Array<'arriba' | 'abajo' | 'izquierda' | 'derecha'>;
};

export default function PantallaSegura({
  children,
  style,
  bordes = ['arriba', 'abajo'],
}: Props) {
  const m = useMargenesSeguros();
  const relleno: ViewStyle = {};
  if (bordes.includes('arriba')) relleno.paddingTop = m.arriba;
  if (bordes.includes('abajo')) relleno.paddingBottom = m.abajo;
  if (bordes.includes('izquierda')) relleno.paddingLeft = m.izquierda;
  if (bordes.includes('derecha')) relleno.paddingRight = m.derecha;

  return <View style={[{ flex: 1 }, style, relleno]}>{children}</View>;
}
